import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { useEffect, useRef, useState } from 'react'

import type { UploadPhase } from '@/components/FileUpload'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CreateStepParams, ScrapeFailureReason } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { castStepToZodForm, getScrapeFailureMessage } from '@/utils/contentModuleForm'
import { awaitScrapeVerdict } from '@/utils/scrapeVerdict'
import {
  checkYouTubeEmbeddable,
  getUrlLikenessError,
  getYouTubeUrlError,
  getYouTubeVideoId,
} from '@/utils/urlValidation'

import type { CMFormSection } from './ContentModuleFormScreen'
import { useStepResources } from './useStepResources'

export function useUploadModalState(props: CMFormSection) {
  const { uiStore, api, bulkUploadStore } = useGlobalState()

  const [isExtracting, setIsExtracting] = useState(false)
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [websiteUrlError, setWebsiteUrlError] = useState<string | null>(null)
  const [embedWarning, setEmbedWarning] = useState<string | null>(null)
  const [pendingFileName, setPendingFileName] = useState<string | null>(null)

  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [processProgressPct, setProcessProgressPct] = useState(0)

  const [stepCreationAbortController, setStepCreationAbortController] = useState<AbortController | null>(null)
  const [createdStepId, setCreatedStepId] = useState<string | null>(null)
  const verdictAbortRef = useRef<AbortController | null>(null)

  useEffect(() => () => verdictAbortRef.current?.abort(), [])

  const { stepResources, uploadedFileResource, sourceFileResource } = useStepResources(createdStepId)

  // Pre-fill website URL when editing an existing step created from a URL. Losing the step
  // leaves the URL alone: a URL that failed to scrape is kept so it can be corrected.
  const [prefilledFrom, setPrefilledFrom] = useState<{
    stepId: typeof props.stepId
    stepResources: typeof stepResources
  }>()
  if (!prefilledFrom || prefilledFrom.stepId !== props.stepId || prefilledFrom.stepResources !== stepResources) {
    setPrefilledFrom({ stepId: props.stepId, stepResources })
    if (props.stepId && stepResources) {
      const sourceResource = stepResources.find(r => (r.type === 'url' || r.type === 'youtube') && r.isSourceFile)
      const sourceUrl = sourceResource?.data || sourceResource?.sourceUrl || ''
      if (websiteUrl !== sourceUrl) setWebsiteUrl(sourceUrl)
    }
  }

  const {
    uploadProgress,
    upload: uploadContent,
    cancel: cancelContentUpload,
  } = useFileUpload({
    onPrepareError: () => {
      props.stepForm.setFieldError('content.file', 'Something went wrong. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadError: () => {
      props.stepForm.setFieldError('content.file', 'Upload failed. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadSuccess: () => props.stepForm.setFieldError('content.file', undefined),
    onUploadCanceled: () => {
      setUploadPhase('idle')
      setProcessProgressPct(0)
    },
  })

  function announceFileCreated() {
    notifications.show({
      title: 'File Created',
      message: 'The file was successfully created.',
      color: 'green',
      classNames: commonClasses.notificationFilled,
      position: 'bottom-left',
    })
  }

  async function createStepWithAbortSupport(params: CreateStepParams) {
    const abortController = new AbortController()
    setStepCreationAbortController(abortController)

    try {
      // A bulk upload into this module is adding files too, so the cached last step may be stale.
      const response = await api.createStep(params, abortController.signal, {
        waitForProcessing: false,
        afterLastStep: bulkUploadStore.jobHoldsModule(params.task_id),
      })

      setCreatedStepId(response.stepId)

      const steps = await props.stepsQuery.refetch()
      // Cancel can land after the create returns but before the refetch does. The modal is
      // closing without this step staged, so it is ours to remove.
      if (abortController.signal.aborted) {
        deleteStepQuietly(response.stepId)
        return null
      }
      const step = steps.data?.find(step => step.stepId === response.stepId)

      // `refetch` takes React Query's default `cancelRefetch: true`, and a settle landing
      // mid-request cancels it silently — the promise resolves with the pre-create list
      // rather than rejecting, so a miss here does not mean the step isn't there. The
      // create response carries what a brand-new step has, so seed from it instead of
      // leaving the form without a `stepId` and the modal gated on a file that exists.
      props.stepForm.setValues(
        castStepToZodForm(
          step ?? {
            stepId: response.stepId,
            name: response.generated?.name,
            introduction: response.generated?.introduction,
          },
        ),
      )

      return response
    } catch (error) {
      if (abortController.signal.aborted) {
        console.log('[UploadStepModal] File creation was cancelled')
        return null
      }

      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      throw error
    } finally {
      setStepCreationAbortController(null)
    }
  }

  // Holds the expert in Add File until the scrape settles, so a URL that can't be read is
  // corrected here instead of turning up later as an errored file in the list.
  async function awaitScrapeFailure(stepId: string) {
    const controller = new AbortController()
    verdictAbortRef.current = controller

    try {
      const verdict = await awaitScrapeVerdict({
        getStatus: () => api.getContentMeta({ step_id: stepId }),
        signal: controller.signal,
      })
      return { aborted: controller.signal.aborted, verdict }
    } catch {
      return { aborted: controller.signal.aborted, verdict: { kind: 'undecided' } as const }
    } finally {
      if (verdictAbortRef.current === controller) verdictAbortRef.current = null
    }
  }

  function deleteStepQuietly(stepId: string) {
    props.stepDeleteMutation.mutate(
      { step_id: stepId, task_id: props.taskId!, showToast: false },
      {
        onError: () =>
          notifications.show({
            title: 'Something went wrong.',
            message: "The file couldn't be removed. Please delete it from the list.",
            color: 'red',
            classNames: commonClasses.notificationFilled,
            position: 'bottom-left',
          }),
      },
    )
  }

  function discardFailedStep(stepId: string, reason?: ScrapeFailureReason) {
    deleteStepQuietly(stepId)
    props.stepForm.setValues(castStepToZodForm())
    setCreatedStepId(null)
    uiStore.setPendingStepDeletion(false)
    setWebsiteUrlError(getScrapeFailureMessage(reason))
  }

  async function handleExtractFromWebsite() {
    if (!websiteUrl.trim()) return

    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
      return
    }

    const youtubeError = getYouTubeUrlError(websiteUrl.trim())
    if (youtubeError) {
      setWebsiteUrlError(youtubeError)
      return
    }
    setWebsiteUrlError(null)
    setEmbedWarning(null)

    setIsExtracting(true)
    setUploadPhase('processing')
    setProcessProgressPct(50)

    try {
      // Non-blocking: warn (but still allow the add) if a YouTube video can't be embedded.
      if (getYouTubeVideoId(websiteUrl.trim())) {
        const embed = await checkYouTubeEmbeddable(websiteUrl.trim())
        if (embed.status === 'not-embeddable') {
          setEmbedWarning(
            embed.reason === 'not-found'
              ? "This video couldn't be found on YouTube. It may have been deleted or the link may be wrong."
              : 'This video may not play for members. It could be private or have embedding turned off by the owner. You can still add it.',
          )
        }
      }

      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
      const response = await createStepWithAbortSupport({
        task_id: props.taskId!,
        url: websiteUrl,
        file_name: websiteUrl,
        use_llm_for_scraping: false,
        preceding_step_id: lastStep?.stepId,
      })
      if (!response) return
      uiStore.setPendingStepDeletion(true)

      if (response.inProcess) {
        const { aborted, verdict } = await awaitScrapeFailure(response.stepId)
        if (aborted || verdict.kind === 'gone') return
        if (verdict.kind === 'failed') {
          discardFailedStep(response.stepId, verdict.progress.reason)
          return
        }
      }
      announceFileCreated()
    } catch (error) {
      console.error('Error extracting from website:', error)
      notifications.show({
        title: 'Extraction Failed',
        message: 'Could not start content extraction. Please check the URL or try again later.',
        color: 'red',
      })
    } finally {
      setIsExtracting(false)
      setUploadPhase('idle')
      setProcessProgressPct(0)
    }
  }

  async function processDroppedFiles(files: FileWithPath[]) {
    const file = files[0]

    setUploadPhase('uploading')
    const uploadResponse = await uploadContent(file)

    if (!uploadResponse) {
      setUploadPhase('idle')
      return
    }

    setPendingFileName(null)
    setUploadPhase('idle')

    try {
      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
      const response = await createStepWithAbortSupport({
        task_id: props.taskId!,
        file_name: file.name,
        s3_file: uploadResponse.filePath,
        preceding_step_id: lastStep?.stepId,
      })
      if (!response) return
      uiStore.setPendingStepDeletion(true)
      announceFileCreated()
    } catch {
      // createStepWithAbortSupport already shows a notification on failure
    }
  }

  function cancelStepCreation() {
    if (stepCreationAbortController) {
      console.log('[UploadStepModal] Cancelling step creation...')
      stepCreationAbortController.abort()
      setStepCreationAbortController(null)
    }
  }

  // In-flight work is stopped on the click rather than once the modal has closed, so nothing
  // that lands during the close can stage a step behind it.
  function cancel() {
    verdictAbortRef.current?.abort()
    cancelStepCreation()
    cancelContentUpload()

    props.closeModal(() => {
      const { stepId } = props.stepForm.getValues()
      const discardStep = uiStore.pendingStepDeletion
      props.stepForm.setValues(castStepToZodForm())
      props.faqActions.resetStepFAQState()
      if (discardStep && stepId) {
        props.stepDeleteMutation.mutate({ step_id: stepId, task_id: props.taskId!, showToast: false })
      }
      uiStore.setPendingStepDeletion(false)
    })
  }

  const isStepCreating = !!stepCreationAbortController
  const hasContent = !!props.stepContentQuery.data
  const contentLoading = uploadPhase !== 'idle' || isStepCreating || props.stepContentQuery.isFetching

  function updateWebsiteUrl(value: string | ((prev: string) => string)) {
    setWebsiteUrl(value)
    if (websiteUrlError) setWebsiteUrlError(null)
    if (embedWarning) setEmbedWarning(null)
  }

  function handleWebsiteUrlBlur() {
    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
    }
  }

  return {
    stepResources,

    isExtracting,
    websiteUrl,
    setWebsiteUrl: updateWebsiteUrl,
    websiteUrlError,
    embedWarning,
    handleWebsiteUrlBlur,
    handleExtractFromWebsite,

    pendingFileName,
    setPendingFileName,

    uploadPhase,
    uploadProgress,
    processProgressPct,

    processDroppedFiles,

    isStepCreating,
    cancel,

    hasContent,
    contentLoading,
    uploadedFileResource,
    sourceFileResource,
  }
}
